import { boundaryJitterMs } from '@/client/utils/refresh-jitter';
import type { SSEApi } from '@/libs/sdk/api/sse';
import { type QueryClient, type QueryKey, focusManager, hashKey } from '@tanstack/react-query';

/** Minimum gap between two SSE-driven refetches of one query key. */
export const SSE_REFETCH_WINDOW_MS = 10_000;

// armedAt: dataUpdateCount when the latest event armed the trailing edge, or
// -1 when a fetch was running then (it may predate the event: refetch anyway).
// windowMs: the window of the latest caller of the key.
type KeyThrottle = { inWindow: boolean; trailing: boolean; armedAt: number; windowMs: number };

// One throttle per query key, shared by every hook on the key: the trade panel
// mounts 5-6 hooks on the selected market, and each hook used to refetch on
// its own (invalidateQueries cancels and restarts a running refetch). Kept per
// QueryClient, so separate clients never share a window.
const throttles = new WeakMap<QueryClient, Map<string, KeyThrottle>>();

const keyThrottles = (queryClient: QueryClient) => {
  let byKey = throttles.get(queryClient);
  if (!byKey) {
    byKey = new Map();
    throttles.set(queryClient, byKey);
  }
  return byKey;
};

// A hidden tab only marks the key stale: refetchOnWindowFocus reloads it when
// the tab is visible again. invalidateQueries does not reject; a failed
// refetch ends in the query's error state.
const invalidateKey = (queryClient: QueryClient, queryKey: QueryKey) => {
  void queryClient.invalidateQueries({ queryKey, refetchType: focusManager.isFocused() ? 'active' : 'none' });
};

/**
 * Invalidates `queryKey` for an SSE event, at most once per window per key.
 * The first event schedules the leading refetch after `leadMs` (the hook's
 * debounce). Events in that delay join it: the back-to-back events of one
 * trade, and the same event on other hooks of the key. Later events do not
 * move it. The window opens at the leading refetch; events inside it give one
 * trailing refetch at its end, which opens the next window. `windowMs` sets
 * the window length (default SSE_REFETCH_WINDOW_MS). When the callers of one
 * key pass different windows, the latest caller's window applies from the
 * next window.
 */
export const throttleSseInvalidation = (
  queryClient: QueryClient,
  queryKey: QueryKey,
  leadMs: number,
  windowMs = SSE_REFETCH_WINDOW_MS
) => {
  const byKey = keyThrottles(queryClient);
  const hash = (queryClient.getDefaultOptions().queries?.queryKeyHashFn ?? hashKey)(queryKey);
  const current = byKey.get(hash);
  if (current) {
    current.windowMs = windowMs;
    if (current.inWindow) {
      const state = queryClient.getQueryState(queryKey);
      current.trailing = true;
      current.armedAt = !state || state.fetchStatus === 'fetching' ? -1 : state.dataUpdateCount;
    }
    return;
  }
  const throttle: KeyThrottle = { inWindow: false, trailing: false, armedAt: -1, windowMs };
  byKey.set(hash, throttle);
  const refetch = () => {
    // invalidateQueries joins an initial fetch (no data yet) and does not
    // restart it. That fetch can predate the event, so the window end refetches.
    const state = queryClient.getQueryState(queryKey);
    const joinsInitialFetch = state?.fetchStatus === 'fetching' && state.data === undefined;
    invalidateKey(queryClient, queryKey);
    throttle.inWindow = true;
    throttle.trailing = joinsInitialFetch;
    throttle.armedAt = -1;
    setTimeout(() => {
      const fetchedSince =
        throttle.armedAt >= 0 && queryClient.getQueryState(queryKey)?.dataUpdateCount !== throttle.armedAt;
      if (throttle.trailing && !fetchedSince) refetch();
      else byKey.delete(hash);
    }, throttle.windowMs);
  };
  setTimeout(refetch, leadMs + boundaryJitterMs());
};

// The first connection id that each SSE client reported. The stream opens for
// the market topics of queries that fetch on mount, so its first connect needs
// no refetch. A later connection id is a reconnect, which can drop events.
const firstConnectionIds = new WeakMap<SSEApi, string>();

// True for the first base:connected of the SSE client (the same answer for every listener).
const isFirstSseConnection = (sse: SSEApi, connectionId: string | undefined) => {
  if (!connectionId) return false;
  if (!firstConnectionIds.has(sse)) firstConnectionIds.set(sse, connectionId);
  return firstConnectionIds.get(sse) === connectionId;
};

/**
 * True when a base:connected needs no refetch of `queryKey`: it is the first
 * connection of the SSE client and the query data is still fresh (fetched on
 * mount). A reconnect, or data older than its stale time (a hook that mounted
 * long before the stream opened), needs the refetch.
 */
export const canSkipSseConnect = (
  queryClient: QueryClient,
  sse: SSEApi,
  queryKey: QueryKey,
  connectionId: string | undefined
) => {
  if (!isFirstSseConnection(sse, connectionId)) return false;
  // The stream often connects before the mount GET returns: that GET covers the connect.
  const query = queryClient.getQueryCache().find({ queryKey, exact: true });
  return !!query && (query.state.fetchStatus === 'fetching' || !query.isStale());
};
